import React, { type FC, useEffect, useRef } from "react";
import styles from "./SpecsAndHighlights.module.scss";
import Search from "./Search";
import Button from "../../../../shared-components/css-modules/lexus/atoms/Button";
import SpecsNavigation from "./SpecsNavigation";
import { useAphBreakpoint } from "../../../../shared-logic/hooks/useBreakpoint";
import Icon from "../../../../shared-components/css-modules/lexus/atoms/Icon";
import { CarConfigLabel } from "../../../utils/labelUtils";
import Text from "../../../../shared-components/css-modules/lexus/atoms/Text";
import type { SpecGroupType } from "../../../hooks/useSpecs";
import type { SpecsNavigationItemType } from "../CarConfigBase";

type SpecsNavigationContainerProps = {
    hideNavigation?: boolean;
    specsNavigationItems: SpecGroupType[] | SpecsNavigationItemType[];
};

const SpecsNavigationContainer: FC<SpecsNavigationContainerProps> = ({ hideNavigation, specsNavigationItems }) => {
    const isMobile = useAphBreakpoint("down", "xl");
    const navRef = useRef<HTMLElement>(null);

    useEffect(() => {
        const popoverNode = navRef.current;
        if (!popoverNode || !isMobile) {
            return;
        }

        const handleToggle = (event: Event) => {
            if ((event as any).newState === "open") {
                document.body.style.overflow = "hidden";
            } else {
                document.body.style.overflow = "";
            }
        };

        popoverNode.addEventListener("toggle", handleToggle);

        return () => {
            popoverNode.removeEventListener("toggle", handleToggle);
            document.body.style.overflow = "";
        };
    }, [isMobile]);

    const handleSpecClick = (_specId: string): void => {
        if (isMobile && navRef.current) {
            navRef.current.hidePopover();
        }
    };

    if (hideNavigation) return null;

    return (
        <section className={styles.navigationContainer}>
            <div className={styles.navigationHeader}>
                <Text variant="apheleia-h2" tag="h1" className={styles.sidebarTitle}>
                    <CarConfigLabel label="carConfigSpecsNavigationTitle" />
                </Text>

                <Search />

                {isMobile && (
                    <Button variant="btn-icon-text" className={styles.menuButton} popovertarget="specs-menu-popover">
                        <Icon variant="menu-two-dashes" sizing="sm" />
                        <CarConfigLabel label="carconfigSpecsMenuButton" />
                    </Button>
                )}
            </div>

            <SpecsNavigation
                ref={navRef}
                id={isMobile ? "specs-menu-popover" : undefined}
                popover={isMobile ? "auto" : undefined}
                SpecsNavigationItems={specsNavigationItems}
                onSpecClick={handleSpecClick}
            />
        </section>
    );
};

export default SpecsNavigationContainer;
